import '@repo/ui/styles/globals.css';
import { PostHogProvider } from '@posthog/react';
import {
  configureApiClient,
  refreshAccessToken,
  setAccessToken,
  setTokenExpiry,
  setUnauthorizedHandler,
} from '@repo/api-client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RootErrorFallback } from './components/shell/RootErrorFallback';
import { RouteSkeleton } from './components/shell/RouteSkeleton';
import { cacheUser, clearUser } from './lib/auth';
import { initPostHog, posthog } from './lib/posthog';
import { routeTree } from './routeTree.gen';

configureApiClient({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000',
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: (failureCount, error) => {
        // Don't retry on 401 — let the interceptor handle it once
        if (error && typeof error === 'object' && 'response' in error) {
          const status = (error as { response?: { status?: number } }).response?.status;
          if (status === 401) return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: (failureCount, error) => {
        if (error && typeof error === 'object' && 'response' in error) {
          const status = (error as { response?: { status?: number } }).response?.status;
          // Never retry HTTP errors — mutations are not idempotent.
          if (status && status >= 400) return false;
        }
        return failureCount < 1;
      },
    },
  },
});

const router = createRouter({
  routeTree,
  defaultErrorComponent: RootErrorFallback,
  defaultPendingComponent: RouteSkeleton,
});

const STARTUP_REFRESH_TIMEOUT_MS = 5_000;

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

let _redirecting = false;
setUnauthorizedHandler(() => {
  if (_redirecting) return;
  _redirecting = true;
  setAccessToken(null);
  setTokenExpiry(null);
  clearUser();
  router.navigate({ to: '/login', search: { redirect: undefined } }).finally(() => {
    _redirecting = false;
  });
});

async function bootstrap() {
  try {
    const data = await refreshAccessToken({ timeout: STARTUP_REFRESH_TIMEOUT_MS });
    cacheUser(data.user);
  } catch {
    // No valid refresh cookie — user logs in. Expected on first visit / expired session.
  }

  // Initialise PostHog before rendering so the provider has a hydrated
  // instance on first paint. No-ops when VITE_POSTHOG_KEY is unset (local dev
  // without credentials or the no-op stub path).
  initPostHog();

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      {/* PostHogProvider wraps QueryClientProvider so any route component can
          read feature flags via useFeatureFlagEnabled. `client` accepts both
          the live posthog-js instance and our no-op stub (cast required because
          the stub only implements the hooks' subset of the PostHog interface). */}
      {/* biome-ignore lint/suspicious/noExplicitAny: PostHog client union doesn't include our minimal stub interface */}
      <PostHogProvider client={posthog as any}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
        </QueryClientProvider>
      </PostHogProvider>
    </StrictMode>,
  );
}

void bootstrap();
